import { useFormContext } from "react-hook-form";
import { useState, useMemo, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Consultation } from "@/types/consultation";
import Model, { IMuscleStats, IExerciseData, Muscle } from "react-body-highlighter";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

// Body parts mapping
const BODY_PARTS = {
  // Back
  trapezius: "Trapezius",
  "upper-back": "Upper Back",
  "lower-back": "Lower Back",
  // Chest
  chest: "Chest",
  // Arms
  biceps: "Biceps",
  triceps: "Triceps",
  forearm: "Forearm",
  "back-deltoids": "Back Deltoids",
  "front-deltoids": "Front Deltoids",
  // Abs
  abs: "Abs",
  obliques: "Obliques",
  // Legs
  adductor: "Adductor",
  hamstring: "Hamstring",
  quadriceps: "Quadriceps",
  abductors: "Abductors",
  calves: "Calves",
  gluteal: "Gluteal",
  // Head
  head: "Head",
  neck: "Neck",
} as const;

const SYMMETRIC_PARTS = [
  "trapezius",
  "biceps",
  "triceps",
  "forearm",
  "back-deltoids",
  "front-deltoids",
  "obliques",
  "adductor",
  "hamstring",
  "quadriceps",
  "abductors",
  "calves",
  "gluteal",
];

const BodyMeasurements = ({ action, open }: FormStepProps) => {
  const { watch, setValue } = useFormContext<Consultation>();
  const [selectedBodyPart, setSelectedBodyPart] = useState<string | null>(null);
  // Store values for single or left/right measurements
  const [measurementValues, setMeasurementValues] = useState<{
    single: string;
    left: string;
    right: string;
  }>({ single: "", left: "", right: "" });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const measurementFormRef = useRef<HTMLFormElement>(null);

  const bodyMeasurements = watch("body_measurements") || {};

  // Convert body measurements to IExerciseData[] format for highlighting
  const exerciseData = useMemo<IExerciseData[]>(() => {
    // Group measurements by base body part
    const groupedMeasurements: Record<string, string[]> = {};

    Object.keys(bodyMeasurements).forEach((key) => {
      let basePart = key;
      let label = "";
      const value = bodyMeasurements[key];

      if (key.startsWith("left_")) {
        basePart = key.replace("left_", "");
        label = `L: ${value}cm`;
      } else if (key.startsWith("right_")) {
        basePart = key.replace("right_", "");
        label = `R: ${value}cm`;
      } else {
        label = `${value}cm`;
      }

      if (!groupedMeasurements[basePart]) {
        groupedMeasurements[basePart] = [];
      }
      groupedMeasurements[basePart].push(label);
    });

    return Object.keys(groupedMeasurements).map((muscle) => {
      const partName = BODY_PARTS[muscle as keyof typeof BODY_PARTS] || muscle;
      const measurements = groupedMeasurements[muscle].join(", ");
      return {
        name: `${partName}: ${measurements}`,
        muscles: [muscle as Muscle],
        frequency: 1,
      };
    });
  }, [bodyMeasurements]);

  // Group measurements for display in the list
  const groupedDisplayItems = useMemo(() => {
    const groups: Record<string, { left?: number; right?: number; single?: number }> = {};

    Object.entries(bodyMeasurements).forEach(([key, value]) => {
      let basePart = key;
      if (key.startsWith("left_")) basePart = key.replace("left_", "");
      else if (key.startsWith("right_")) basePart = key.replace("right_", "");

      if (!groups[basePart]) groups[basePart] = {};

      const numValue = typeof value === 'string' ? parseFloat(value) : (value as number);

      if (key.startsWith("left_")) groups[basePart].left = numValue;
      else if (key.startsWith("right_")) groups[basePart].right = numValue;
      else groups[basePart].single = numValue;
    });

    // Sort by order in BODY_PARTS definition
    const orderedKeys = Object.keys(BODY_PARTS);

    return Object.entries(groups)
      .sort(([keyA], [keyB]) => {
        const indexA = orderedKeys.indexOf(keyA);
        const indexB = orderedKeys.indexOf(keyB);
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
      })
      .map(([basePart, values]) => ({
        basePart,
        name: BODY_PARTS[basePart as keyof typeof BODY_PARTS] || basePart,
        values
      }));
  }, [bodyMeasurements]);

  // Handle body part click
  const handleMuscleClick = ({ muscle }: IMuscleStats) => {
    const bodyPartKey = muscle as keyof typeof BODY_PARTS;
    if (BODY_PARTS[bodyPartKey]) {
      setSelectedBodyPart(bodyPartKey);

      const isSymmetric = SYMMETRIC_PARTS.includes(bodyPartKey);
      setMeasurementValues({
        single: isSymmetric ? "" : (bodyMeasurements[bodyPartKey]?.toString() || ""),
        left: isSymmetric ? (bodyMeasurements[`left_${bodyPartKey}`]?.toString() || "") : "",
        right: isSymmetric ? (bodyMeasurements[`right_${bodyPartKey}`]?.toString() || "") : "",
      });

      setIsDialogOpen(true);
    }
  };

  // Handle save measurement
  const handleSaveMeasurement = () => {
    if (selectedBodyPart) {
      const updated = { ...bodyMeasurements };
      const isSymmetric = SYMMETRIC_PARTS.includes(selectedBodyPart);

      if (isSymmetric) {
        // Require both left and right measurements
        const leftVal = parseFloat(measurementValues.left);
        const rightVal = parseFloat(measurementValues.right);

        if (
          !measurementValues.left ||
          !measurementValues.right ||
          isNaN(leftVal) ||
          leftVal <= 0 ||
          isNaN(rightVal) ||
          rightVal <= 0
        ) {
          return;
        }

        updated[`left_${selectedBodyPart}`] = leftVal;
        updated[`right_${selectedBodyPart}`] = rightVal;
      } else {
        // Handle Single
        if (measurementValues.single && parseFloat(measurementValues.single) > 0) {
          updated[selectedBodyPart] = parseFloat(measurementValues.single);
        } else {
          delete updated[selectedBodyPart];
        }
      }

      setValue("body_measurements", updated);
      setIsDialogOpen(false);
      setSelectedBodyPart(null);
      setMeasurementValues({ single: "", left: "", right: "" });
    }
  };

  // Handle form submission (Enter key)
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleSaveMeasurement();
  };

  // Handle remove measurement
  const handleRemoveMeasurement = () => {
    if (selectedBodyPart) {
      const updated = { ...bodyMeasurements };
      const isSymmetric = SYMMETRIC_PARTS.includes(selectedBodyPart);

      if (isSymmetric) {
        delete updated[`left_${selectedBodyPart}`];
        delete updated[`right_${selectedBodyPart}`];
      } else {
        delete updated[selectedBodyPart];
      }

      setValue("body_measurements", updated);
      setIsDialogOpen(false);
      setSelectedBodyPart(null);
      setMeasurementValues({ single: "", left: "", right: "" });
    }
  };

  // Handle edit measurement from saved list
  const handleEditMeasurement = (key: string) => {
    // Determine base part name (remove left_ or right_ prefix)
    let basePart = key;
    if (key.startsWith("left_")) basePart = key.replace("left_", "");
    if (key.startsWith("right_")) basePart = key.replace("right_", "");

    setSelectedBodyPart(basePart);

    const isSymmetric = SYMMETRIC_PARTS.includes(basePart);
    setMeasurementValues({
      single: isSymmetric ? "" : (bodyMeasurements[basePart]?.toString() || ""),
      left: isSymmetric ? (bodyMeasurements[`left_${basePart}`]?.toString() || "") : "",
      right: isSymmetric ? (bodyMeasurements[`right_${basePart}`]?.toString() || "") : "",
    });

    setIsDialogOpen(true);
  };

  // Handle remove measurement from saved list
  const handleRemoveFromList = (basePart: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering edit when clicking remove
    const updated = { ...bodyMeasurements };

    if (SYMMETRIC_PARTS.includes(basePart)) {
      delete updated[`left_${basePart}`];
      delete updated[`right_${basePart}`];
    } else {
      delete updated[basePart];
    }

    setValue("body_measurements", updated);
  };

  // Get selected body part display name
  const selectedBodyPartName = selectedBodyPart
    ? BODY_PARTS[selectedBodyPart as keyof typeof BODY_PARTS]
    : "";

  return (
    <div className="">
      {/* Add CSS for hover effects on body parts */}
      <style>{`
        .rbh-wrapper svg polygon {
          transition: fill 0.2s ease, opacity 0.2s ease, filter 0.2s ease;
        }
        .rbh-wrapper svg polygon:hover {
          filter: brightness(0.7);
          opacity: 0.9;
        }
      `}</style>

      <Card className="p-4">
        <p className="font-medium text-sm">Body Measurements</p>
        <p className="text-xs text-gray-500 mt-1">
          Click on any body part to add measurements (in centimeters).
        </p>
        <Separator className="h-[1px] mt-2" />

        {/* Side by side front and back views */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {/* Front View */}
          <div className="flex flex-col items-center">
            <p className="text-sm font-medium mb-2">Front View</p>
            <div className="flex justify-center items-center w-full">
              <Model
                data={exerciseData}
                onClick={handleMuscleClick}
                highlightedColors={["#77dd77", "#60a5fa"]} // Green/Blue colors for selected parts
                style={{ width: "100%", maxWidth: "300px", height: "450px" }}
                svgStyle={{ cursor: "pointer" }}
              />
            </div>
          </div>

          {/* Back View */}
          <div className="flex flex-col items-center">
            <p className="text-sm font-medium mb-2">Back View</p>
            <div className="flex justify-center items-center w-full">
              <Model
                type="posterior"
                data={exerciseData}
                onClick={handleMuscleClick}
                highlightedColors={["#77dd77", "#60a5fa"]} // Green/Blue colors for selected parts
                style={{ width: "100%", maxWidth: "300px", height: "450px" }}
                svgStyle={{ cursor: "pointer" }}
              />
            </div>
          </div>
        </div>

        {/* Info about views */}
        <div className="mt-4 p-3 bg-blue-50 rounded text-xs text-gray-600">
          <p className="font-medium mb-1">Note:</p>
          <p>
            Front and back views show different body parts. Measurements are shared between views -
            if you measure a part on the front view, it will also appear highlighted on the back view if that part is visible there.
            Some parts like head, neck, and some leg muscles appear on both views.
          </p>
        </div>

        {/* Display saved measurements */}
        {groupedDisplayItems.length > 0 && (
          <div className="mt-6">
            <p className="text-sm font-medium mb-3">Saved Measurements ({groupedDisplayItems.length})</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {groupedDisplayItems.map(({ basePart, name, values }) => (
                <div
                  key={basePart}
                  onClick={() => handleEditMeasurement(basePart)}
                  className="relative flex flex-col p-3 bg-white border rounded-lg shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-pointer group"
                >
                  <div className="flex justify-between items-start">
                    <span className="font-semibold text-gray-700 text-sm truncate pr-6" title={name}>
                      {name}
                    </span>
                    <button
                      onClick={(e) => handleRemoveFromList(basePart, e)}
                      className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                      type="button"
                      title="Remove"
                    >
                      <i className="fa fa-trash text-xs"></i>
                    </button>
                  </div>
                  <div className="mt-2 text-sm">
                    {values.single !== undefined ? (
                      <div className="font-medium text-gray-900">{values.single} cm</div>
                    ) : (
                      <div className="flex gap-2 w-full">
                        {values.left !== undefined && (
                          <div className="flex flex-col flex-1 bg-gray-50 p-1.5 rounded border border-gray-100">
                            <span className="text-[10px] uppercase text-gray-500 font-bold">Left</span>
                            <span className="font-medium text-gray-900">{values.left} cm</span>
                          </div>
                        )}
                        {values.right !== undefined && (
                          <div className="flex flex-col flex-1 bg-gray-50 p-1.5 rounded border border-gray-100">
                            <span className="text-[10px] uppercase text-gray-500 font-bold">Right</span>
                            <span className="font-medium text-gray-900">{values.right} cm</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Measurement Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {selectedBodyPartName
                ? `Measure ${selectedBodyPartName}`
                : "Add Measurement"}
            </DialogTitle>
          </DialogHeader>
          <form
            ref={measurementFormRef}
            onSubmit={handleFormSubmit}
            onKeyDown={(e) => {
              // Prevent form submission from bubbling to parent form
              if (e.key === "Enter" && e.target === e.currentTarget) {
                e.stopPropagation();
              }
            }}
          >
            <div className="space-y-4 py-4">
              {selectedBodyPart && SYMMETRIC_PARTS.includes(selectedBodyPart) ? (
                <>
                  <FloatingLabelInput
                    id="measurement-left"
                    type="number"
                    label={`Left ${selectedBodyPartName} (cm)`}
                    value={measurementValues.left}
                    onChange={(e) => setMeasurementValues(prev => ({ ...prev, left: e.target.value }))}
                    min="0"
                    step="0.1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.stopPropagation();
                    }}
                  />
                  <FloatingLabelInput
                    id="measurement-right"
                    type="number"
                    label={`Right ${selectedBodyPartName} (cm)`}
                    value={measurementValues.right}
                    onChange={(e) => setMeasurementValues(prev => ({ ...prev, right: e.target.value }))}
                    min="0"
                    step="0.1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.stopPropagation();
                    }}
                  />
                </>
              ) : (
                <FloatingLabelInput
                  id="measurement"
                  type="number"
                  label="Measurement (cm)"
                  value={measurementValues.single}
                  onChange={(e) => setMeasurementValues(prev => ({ ...prev, single: e.target.value }))}
                  min="0"
                  step="0.1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.stopPropagation();
                  }}
                />
              )}
            </div>
            <DialogFooter>
              {(selectedBodyPart && (
                SYMMETRIC_PARTS.includes(selectedBodyPart)
                  ? (bodyMeasurements[`left_${selectedBodyPart}`] || bodyMeasurements[`right_${selectedBodyPart}`])
                  : bodyMeasurements[selectedBodyPart]
              )) && (
                  <Button
                    variant="destructive"
                    onClick={handleRemoveMeasurement}
                    type="button"
                  >
                    Remove All
                  </Button>
                )}
              <Button
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  setSelectedBodyPart(null);
                  setMeasurementValues({ single: "", left: "", right: "" });
                }}
                type="button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  selectedBodyPart && SYMMETRIC_PARTS.includes(selectedBodyPart)
                    ? !measurementValues.left ||
                    !measurementValues.right ||
                    parseFloat(measurementValues.left) <= 0 ||
                    parseFloat(measurementValues.right) <= 0
                    : !measurementValues.single ||
                    parseFloat(measurementValues.single) <= 0
                }
              >
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BodyMeasurements;

